import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, fetchData } from '../helpers/api';
import { Calendar, ArrowLeft, ChevronRight, ChevronLeft, Video, Image } from 'lucide-react';
import FormatDate from '../utils/dateTools';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const SinglePatrimoineActualitePage = () => {
    const { articleId } = useParams();
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [activeTab, setActiveTab] = useState('images'); // 'images' or 'video'

    useEffect(() => {
        const fetchArticleData = async () => {
            try {
                setLoading(true);
                
                // Fetch all articles
                const allArticles = await fetchData('/api/patrimoine-actualites?populate=*');
                const currentArticle = await fetchData(`/api/patrimoine-actualites/${articleId}?populate=*`);
                
                if (currentArticle) {
                    setArticle(currentArticle);
                    
                    // Set active tab based on available media
                    if (currentArticle.video && currentArticle.video.url && (!currentArticle.images || currentArticle.images.length === 0)) {
                        setActiveTab('video');
                    }

                    // Fetch video URL if available
                    if (currentArticle.video && currentArticle.video.url) {
                        try {
                            const videoLink = API_URL + currentArticle.video.url;
                            setVideoUrl(videoLink);
                            setActiveTab('video');
                        } catch (error) {
                            console.error('Error fetching video link:', error);
                        }
                    }
                    
                    // Get related articles (excluding current)
                    const related = allArticles
                        .filter(a => a.documentId !== articleId)
                        .sort(() => 0.5 - Math.random()) // Simple randomization
                        .slice(0, 3); // Get three random related articles
                    
                    setRelatedArticles(related);
                }
            } catch (error) {
                console.error('Error fetching article data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (articleId) {
            fetchArticleData();
        }
    }, [articleId]);

    // Image viewer navigation functions
    const openImageViewer = (index) => {
        setSelectedImageIndex(index);
        setIsImageViewerOpen(true);
    };

    const navigateImage = (direction) => {
        if (!article || !article.images) return;

        const totalImages = article.images.length;
        let newIndex = selectedImageIndex;

        if (direction === 'next') {
            newIndex = (selectedImageIndex + 1) % totalImages;
        } else {
            newIndex = (selectedImageIndex - 1 + totalImages) % totalImages;
        }

        setSelectedImageIndex(newIndex);
    };

    // Function to check if media exists
    const hasImages = article?.images && article.images.length > 0;
    const hasVideo = !!videoUrl;

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="container py-5 text-center">
                <h1>Article non trouvé</h1>
                <p>Désolé, l'article que vous recherchez n'existe pas.</p>
                <a href="/patrimoine-actualites" className="btn btn-warning">
                    Retour aux articles
                </a>
            </div>
        );
    }

    return (
        <div className="single-patrimoine-article">
            {/* Header with Back Button */}
            <div className="container py-4 mt-4">
                <a
                    href="/patrimoine-actualites"
                    className="btn btn-outline-dark rounded-pill mb-4 d-inline-flex align-items-center"
                >
                    <ArrowLeft size={20} className="me-2" />
                    Retour aux articles
                </a>
            </div>

            {/* Image Viewer Modal */}
            {hasImages && (
                <div
                    className={`modal fade ${isImageViewerOpen ? 'show d-block' : ''}`}
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 2000 }}
                >
                    <div className="modal-dialog modal-fullscreen">
                        <div className="modal-content bg-transparent border-0">
                            <div className="modal-header border-0">
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setIsImageViewerOpen(false)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body d-flex justify-content-center align-items-center position-relative">
                                {article.images.length > 1 && (
                                    <button
                                        className="btn btn-link text-white position-absolute start-0 top-50 translate-middle-y"
                                        onClick={() => navigateImage('prev')}
                                    >
                                        <ChevronLeft size={48} />
                                    </button>
                                )}
                                <img
                                    src={API_URL + article.images[selectedImageIndex].url}
                                    alt={`${article.title} - Image ${selectedImageIndex + 1}`}
                                    className="img-fluid"
                                    style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
                                />
                                {article.images.length > 1 && (
                                    <button
                                        className="btn btn-link text-white position-absolute end-0 top-50 translate-middle-y"
                                        onClick={() => navigateImage('next')}
                                    >
                                        <ChevronRight size={48} />
                                    </button>
                                )}
                            </div>
                            <div className="modal-footer border-0 justify-content-center text-white">
                                <span>{`${selectedImageIndex + 1} / ${article.images.length}`}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Article Content */}
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <article>
                            <header className="mb-4">
                                <h1 className="display-5 fw-bold mb-3">{article.title}</h1>
                                <div className="text-muted d-flex align-items-center mb-4">
                                    <Calendar size={18} className="me-2" />
                                    <span>Publié le <FormatDate dateString={article.publishedOn} format="long" /></span>
                                </div>
                            </header>

                            {/* Media Tab Navigation - Only show if both video and images exist */}
                            {hasVideo && hasImages && (
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button 
                                            className={`nav-link ${activeTab === 'images' ? 'active fw-bold' : ''}`} 
                                            onClick={() => setActiveTab('images')}
                                        >
                                            <Image size={18} className="me-2" />
                                            Photos ({article.images.length})
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button 
                                            className={`nav-link ${activeTab === 'video' ? 'active fw-bold' : ''}`} 
                                            onClick={() => setActiveTab('video')}
                                        >
                                            <Video size={18} className="me-2" />
                                            Vidéo
                                        </button>
                                    </li>
                                </ul>
                            )}

                            {/* Main Image or Video */}
                            {(activeTab === 'images' && hasImages) && (
                                <div className="mb-4">
                                    <div 
                                        className="position-relative rounded overflow-hidden shadow-sm mb-2 cursor-pointer"
                                        onClick={() => openImageViewer(0)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <img
                                            src={API_URL + article.images[0]?.url}
                                            alt={article.title}
                                            className="img-fluid w-100"
                                            style={{ maxHeight: '500px', objectFit: 'cover' }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Video Tab Content */}
                            {(activeTab === 'video' && hasVideo) && (
                                <div className="mb-4">
                                    <div className="rounded shadow-lg overflow-hidden">
                                        <div className="position-relative" style={{ paddingBottom: '56.25%', height: '0', overflow: 'hidden' }}>
                                            <video 
                                                controls 
                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                style={{ objectFit: 'cover' }}
                                                poster={hasImages ? API_URL + article.images[0]?.url : null}
                                            >
                                                <source src={videoUrl} type="video/mp4" />
                                                Votre navigateur ne prend pas en charge la lecture de vidéos.
                                            </video>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Article Content */}
                            <div className="article-content">
                                <TruncatableHtmlContent
                                    html={article.content}
                                />
                            </div>

                            {/* Image Gallery (if more than one image) */}
                            {article.images && article.images.length > 1 && (
                                <div className="mt-5">
                                    <h3 className="h5 fw-bold mb-3">Galerie photos</h3>
                                    <div className="row row-cols-2 row-cols-md-4 g-2">
                                        {article.images.map((image, index) => (
                                            <div key={index} className="col">
                                                <div 
                                                    className="ratio ratio-1x1 rounded overflow-hidden shadow-sm cursor-pointer"
                                                    onClick={() => openImageViewer(index)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <img
                                                        src={API_URL + image.url}
                                                        alt={`${article.title} - Image ${index + 1}`}
                                                        className="object-fit-cover w-100 h-100"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </article>
                    </div>
                </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
                <section className="bg-light py-5 mt-5">
                    <div className="container">
                        <h2 className="h3 fw-bold mb-4">Autres articles</h2>
                        <div className="row row-cols-1 row-cols-md-3 g-4">
                            {relatedArticles.map(relatedArticle => (
                                <div key={relatedArticle.id} className="col">
                                    <div className="card border-0 shadow h-100 hover-translate-up transition">
                                        {relatedArticle.images && relatedArticle.images.length > 0 && (
                                            <img
                                                src={API_URL + relatedArticle.images[0]?.url}
                                                alt={relatedArticle.images[0]?.alt || relatedArticle.title}
                                                className="card-img-top"
                                                style={{ objectFit: 'cover', height: '200px' }}
                                            />
                                        )}
                                        <div className="card-body d-flex flex-column">
                                            <div className="d-flex align-items-center text-muted small mb-3">
                                                <Calendar size={16} className="me-2" />
                                                <FormatDate dateString={relatedArticle.publishedOn} format="long" />
                                            </div>
                                            <h3 className="fs-5 fw-bold mb-3">{relatedArticle.title}</h3>
                                            <a
                                                href={`/patrimoine-actualites/${relatedArticle.documentId}`}
                                                className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none mt-auto"
                                            >
                                                Lire la suite
                                                <ChevronRight size={16} className="ms-1" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default SinglePatrimoineActualitePage;